/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <section>
    <div class="row">
      <div
        v-if="loaded"
        class="col-sm-6 col-sm-offset-3"
        aria-labelledby="subscriptionSubHeader"
      >
        <div v-if="(paymentTarget || displayGroup) && !isOrgLevelManager">
          <h1
            v-if="!paymentTarget"
            id="subscriptionSubHeader"
          >
            Subscription
          </h1>
          <p v-if="!paymentTarget">
            Your access to {{ org.name }} ({{ displayGroup.name }})
            <span v-if="displayGroup.membershipStatus === 'active'">
              is <strong><em>active</em></strong>
              <span v-if="displayGroupMembershipExpires">
                until
                <strong>{{ displayGroup.membershipExpires | dateShort }}</strong>.
              </span>
            </span>
            <span v-if="displayGroup.membershipStatus !== 'active'">
              <span v-if="!displayGroup.membershipExpires">
                is <strong><em>not active yet</em></strong>.
              </span>
              <span v-if="displayGroup.membershipExpires">
                has <strong><em>expired</em></strong>
                since <strong>{{ displayGroup.membershipExpires | dateShort }}</strong>.
              </span>
            </span>
          </p>
        </div>
        <div v-else-if="isOrgLevelAdmin">
          <h1 id="subscriptionOLA">
            Subscription
          </h1>
          <p>
            You are signed up for an account as an administrator.
            <span v-if="!isSpeaker">
              This means you will be able to create new
              {{ isEdu ? 'classes and instructors' : 'groups and coaches' }} but will not be able to add new
              recordings yourself.
            </span><br>
          </p>
        </div>
        <div v-else-if="isOrgLevelManager">
          <h1 id="subscriptionOLM">
            Subscription
          </h1>
          <p>
            You are signed up for an account as {{ isEdu ? 'an instructor' : 'a coach' }}.
            <span v-if="!isSpeaker">
              This means you will be able to create new
              {{ isEdu ? 'classes' : 'groups' }} but will not be able to add new
              recordings yourself.
            </span><br>
          </p>
        </div>
        <div v-else>
          <h1 id="subscriptionOther">
            Subscription
          </h1>
          <p>
            You can click on a page from the menu above. If you are having issues with your
            account, please contact your organization's administrator or
            support@bigtincan.com
          </p>
        </div>

        <div v-if="paymentTarget || (speakerGroup && !managerGroup && plans.length && speakerGroup.membershipStatus !== 'active' && groups.length == 1)">
          <p>
            Select a plan to complete your registration or renew:
          </p>
          <table class="table table-bordered table-striped">
            <thead>
              <tr>
                <th>Plan</th>
                <th v-if="!isCouponOnly">
                  Price
                </th>
                <th v-if="!isCouponOnly">
                  Expiration Date
                </th>
                <th v-if="!isCouponOnly">
                  Auto-renew
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in plans">
                <td>
                  <label :for="`plan-${plan.planId}`">
                    <input
                      :id="`plan-${plan.planId}`"
                      v-model="selectedPlan"
                      type="radio"
                      :value="plan"
                      :checked="isCouponOnly"
                    >
                    {{ plan.name }}
                  </label>
                </td>
                <td v-if="!isCouponOnly">
                  ${{ (plan.price/100).toFixed(2) }}
                </td>
                <td v-if="!isCouponOnly">
                  {{ plan.expirationDate | dateShort }}
                </td>
                <td v-if="!isCouponOnly">
                  {{ plan.recurring ? 'Yes' : 'No' }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="selectedPlan || isCouponOnly">
            <h2
              id="subscription-nav"
              class="sr-only"
            >
              Subscription Navigation
            </h2>
            <nav aria-labelledby="subscription-nav">
              <ul
                class="nav nav-tabs"
              >
                <li
                  v-if="!isCouponOnly"
                  :class="{ active: tab === 'credit card payment' }"
                >
                  <a
                    id="creditcardpaymentlink"
                    ref="creditcardpaymentlink"
                    href="#"
                    @click.prevent="invalidData = false; loadTab('credit card payment')"
                    @keypress="invalidData = false; loadTab('credit card payment', $event)"
                  >
                    Pay by Credit Card
                  </a>
                </li>
                <li
                  :class="{ active: tab === 'prepaid card' }"
                >
                  <a
                    id="prepaidcardlink"
                    ref="prepaidcardlink"
                    href="#"
                    @click.prevent="invalidData = false; loadTab('prepaid card')"
                    @keypress="invalidData = false; loadTab('prepaid card', $event)"
                  >
                    Use Prepaid Card
                  </a>
                </li>
              </ul>
            </nav>
            <div
              v-if="saving"
              class="tab-well text-center"
            >
              <h2>
                <svg
                  class="icon-refresh rotate large-spinner"
                >
                  <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
                </svg>
                Updating subscription...
              </h2>
            </div>
            <div v-if="!saving">
              <div
                v-show="tab === 'credit card payment'"
                class="tab-well"
              >
                <h2
                  id="creditcardpaymentheader"
                  class="sr-only"
                >
                  Payment Form
                </h2>
                <div
                  v-if="paymentFailed"
                  class="help-block"
                >
                  <span class="text-danger">
                    <svg
                      class="icon-alert-triangle"
                    >
                      <use xlink:href="/static/svg-symbols.svg#icon-alert-triangle" />
                    </svg> Payment failed: {{ paymentFailureMessage }}
                  </span>
                </div>
                <payment-form
                  v-if="!isCouponOnly"
                  :plan="selectedPlan"
                  :callback="subscribeWithStripe"
                />
              </div>
              <div
                v-if="tab === 'prepaid card'"
                class="tab-well"
              >
                <h2
                  id="prepaidcardheader"
                  class="sr-only"
                >
                  Prepaid Card Form
                </h2>
                <form @submit.prevent="subscribeWithAccessCode">
                  <p>
                    If you already purchased a Prepaid Card, this is where you can enter the code printed on your card.<br>
                    A Prepaid Card code can only be used one time.
                  </p>
                  <div class="form-group">
                    <label for="subscriptionAccessCode">
                      Prepaid Card Code
                    </label>
                    <input
                      id="subscriptionAccessCode"
                      v-model.trim="accessCode"
                      type="text"
                      class="form-control"
                      placeholder=""
                      required
                    >
                  </div>
                  <div class="help-block">
                    <p
                      v-if="invalidData"
                      class="text-danger"
                    >
                      Subscription update failed. Please double check the code you entered and try again
                    </p>
                  </div>
                  <p>
                    <button class="btn btn-primary">
                      Use Prepaid Card Code
                    </button>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="(displayGroup && displayGroup.membershipStatus === 'active') || isOrgLevelAdmin || isOrgLevelManager"
          text-align="center"
        >
          <div v-if="this.$store.getters.org && this.$store.getters.org.settings && this.$store.getters.org.settings.Assignments && showEmailSignup">
            <h2>Setup Email Notifications</h2>
            In order to get the most from your VoiceVibes account, we recommend enabling email
            notifications. This ensures you will receive a notification email
            <span v-if="isGroupManager">
              when a recording is flagged for your review.
            </span>
            <span v-else>
              when you receive a new assignment and when someone leaves you feedback on your recordings.
            </span>
            Email notifications can always be modified or disabled later via your
            <router-link :to="{ name: 'email-notifications' }">
              Account Settings Page.
            </router-link>
            <br><br>

            <form
              @submit.prevent="saveNotifications"
            >
              <div class="form-group">
                <label
                  class="checkbox-inline"
                  :for="emailNotifications"
                >
                  <input
                    id="emailNotifications"
                    v-model="emailNotifications"
                    type="checkbox"
                    value="1"
                  > Enable Email Notifications for my account
                </label>
              </div>

              <loading-button
                type="submit"
                :loading="saving"
              >
                Continue
              </loading-button>
            </form>
          </div>
          <div v-else-if="!paymentTarget">
            <router-link
              :to="{ name: homepage }"
              class="btn btn-primary"
            >
              Continue
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div
        v-if="groups && groups.length > 1 && !paymentTarget"
        class="col-md-12"
        style="margin-top: 20px;"
      >
        <h2>List of All Subscriptions</h2>
        <table class="table table-striped table-bordered table-condensed">
          <thead>
            <tr>
              <th>Group</th>
              <th>Status</th>
              <th v-if="!isOrgExpiration">
                Expiration Date
              </th>
              <th v-if="!isOrgExpiration && plans.length">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="g in groups">
              <td>
                {{ g.name }}
              </td>
              <td>
                {{ g.membershipStatus | capitalize }} {{ getRoletype(g) }}
              </td>
              <td v-if="!isOrgExpiration && g.membershipPerpetual">
                Does not expire
              </td>
              <td v-else-if="!isOrgExpiration">
                {{ g.membershipExpires | date }}
              </td>
              <td v-if="!isOrgExpiration && plans.length && !canRenew(g)">
                -
              </td>
              <td v-if="!isOrgExpiration && plans.length && canRenew(g)">
                <button
                  class="btn btn-primary"
                  @click.prevent="setPaymentTarget(g)"
                >
                  {{ g.membershipExpires ? 'Renew' : 'Activate' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<style lang="less" scoped>
  .icon-alert-triangle {
    height: 14px; width: 14px;
  }
  .nav li {
    width: 150px;
  }
</style>

<script>
import PaymentForm from '../forms/PaymentForm';
import service from '../../services/groups';
import BasePage from './BasePage';

export default {
  components: { PaymentForm },
  mixins: [BasePage],
  data() {
    return {
      tab: 'credit card payment',
      invalidData: false,
      paymentFailed: false,
      paymentFailureMessage: '',
      selectedPlan: null,
      saving: false,
      accessCode: '',
      emailNotifications: 1,
      paymentTarget: null,
    };
  },
  computed: {
    showEmailSignup: {
      cache: false,
      get() {
        if (this.notificationsEnabled) return false;
        return this.showEmailNotifications;
      }
    },
    notificationsEnabled: {
      cache: false,
      get() {
        let enabled = false;
        this.$store.getters.appConfig.emailNotifications.forEach((item) => {
          if (this.me[item] && this.me[item] !== '0') {
            enabled = true;
          }
        });
        return enabled;
      }
    },
    loaded: {
      cache: false,
      get() {
        return this.basePageLoaded && this.plans;
      }
    },
    displayGroup: {
      cache: false,
      get() {
        return this.speakerGroup ? this.speakerGroup : this.managerGroup;
      }
    },
    displayGroupMembershipExpires: {
      cache: false,
      get() {
        if (!this.displayGroup.membershipExpires) {
          return false;
        }
        const in15Years = this.moment().add(15, 'years');
        return this.moment(this.displayGroup.membershipExpires).isBefore(in15Years);
      }
    },
    managerGroup: {
      cache: false,
      get() {

        if (!this.$store.getters.groups.length) {
          return;
        }
        const filter = (group) => {
          return this._.includes(group.roles, 'manager') && group.membershipStatus === 'active';
        };
        let group = this._.find(this.$store.getters.groups, filter);

        if (!group) {
          // We pick the most recently expired manager group
          group = this._.find(
            this._.sortBy(this.$store.getters.groups, 'membershipExpires').reverse(),
            (group) => {
              return this._.includes(group.roles, 'manager');
            });
        }
        return group;
      }
    },
    groups: {
      cache: false,
      get() {
        return this.$store.getters.groups;
      }
    },
    speakerGroup: {
      cache: false,
      get() {
        if (!this.$store.getters.groups.length) {
          return;
        }
        const filter = this.$route.params.groupId
          ? group => group.groupId === this.$route.params.groupId
          : group => {
            return this._.includes(group.roles, 'speaker') && group.membershipStatus === 'active';
          };
        let group = this._.find(this.$store.getters.groups, filter);

        if (!group) {
          // We pick the group which expired most recently
          group = this._.find(
            this._.sortBy(this.$store.getters.groups, 'membershipExpires').reverse(),
            (group) => {
              return this._.includes(group.roles, 'speaker');
            });
        }

        // Finally, just default to the first group, if needed
        if (!group) {
          group = this._.sortBy(this.$store.getters.groups, 'membershipExpires').reverse();
          group = group[0];
        }

        return group;
      }
    },
    isCouponOnly() {
      // If pricing includes a $0 plan, hide Stripe option
      return this.plans && this.plans[0] && !this.plans[0].price;
    },
    plans() {
      const plans = this.$store.getters.org && this.$store.getters.org.plans;
      return this._.sortBy(plans, plan => {
        plan.expirationDate = this.moment().add(plan.duration).toDate();
        return -plan.price;
      });
    }
  },
  methods: {
    setPaymentTarget(g) {
      const expires = this.moment(g.membershipExpires).format('ll');
      if (g.membershipStatus === 'active') {
        if (window.confirm(`Your account is active and does not expire until ${expires}. Are you sure you want to renew?`)) {
          this.paymentTarget = g;
        }
        return;
      }
      this.paymentTarget = g;
    },
    getRoletype(g) {
      return (g.roles.length === 1 && g.roles.indexOf('speaker') === 0) ? 'Speaker' : 'Coach';
    },
    canRenew(g) {
      return (g.roles.length === 1 && g.roles.indexOf('speaker') === 0 && !g.membershipPerpetual);
    },
    basePageCreated() {
      if (this.isCouponOnly) {
        this.tab = 'prepaid card';
        this.selectedPlan = this.plans[0];
      }
    },
    saveNotifications() {
      if (!this.emailNotifications) {
        return this.$router.push({ name: this.homepage });
      }

      const user = {
        recordingSubmittedFrequency: this.isGroupManager ? 1 : 0,
        feedbackReceivedFrequency: this.isSpeaker ? 1 : 0,
        recordingProcessedFrequency: this.isSpeaker ? 1 : 0,
        recordingSharedFrequency: this.isSpeaker ? 1 : 0,
        assignmentReceivedFrequency: this.isSpeaker ? 1 : 0,
        assignmentCompletedFrequency: this.isGroupManager ? 1 : 0,
      };

      this.$store.dispatch('updateUserEmailPreferences', user).then(
        () => this.$router.push({ name: this.homepage })
      );
    },
    subscribeWithAccessCode() {
      this.subscribe({ accessCode: this.accessCode });
    },
    subscribeWithStripe(stripeToken) {
      this.subscribe({ stripeToken });
    },
    subscribe(opts) {
      this.saving = true;
      this.invalidData = false;
      this.paymentFailed = false;
      const groupId = this.paymentTarget ? this.paymentTarget.groupId : this.speakerGroup.groupId;

      service.updateSubscription(groupId, this._.merge({
        planId: this.selectedPlan.planId,
      }, opts))
        .then(data => {
          this.paymentTarget = null;

          const expires = this.moment(data.expires).format('ll');
          let text =  `Your account has been activated until ${expires}.`;
          if (data.transactionId) {
            text += ` Your transaction reference is ${data.transactionId}. A receipt has been sent to you by email.`;
          }
          window.setTimeout(() => this.$store.dispatch('listGroups'), 1000);
          return this.$store.dispatch('notify', {
            type: 'success',
            subject: 'Subscription updated',
            text
          });
        })
        .catch(errors => {
          if (window.Raven) {
            const message = errors[0] && errors[0].message ? errors[0].message : '';
            const stripeToken = opts && opts.stripeToken ? opts.stripeToken : '';

            window.Raven.captureMessage('Subscription Update Failed', {tags: {message, stripeToken}});
          }
          if (errors[0].$status === 400) {
            this.invalidData = true;
          } else if (errors[0].$status === 402) {
            this.paymentFailed = true;
            this.paymentFailureMessage = errors[0].message;
          }
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>
